<template>
  <section class="dp-layout cgc-summary">
    <section class="dp-left">
      <DepartmentPane directory-scope="teaching" pane-title="家校通讯录" @tree-node-click="treeNodeClick" />
    </section>
    <section class="dp-right">
      <div class="toolbar-filter-bar">
        <el-form :inline="true" class="toolbar-filter-form">
          <div class="toolbar-filter-fields">
            <el-form-item label="竞赛小组">
              <el-input v-model="filters.name" clearable placeholder="支持模糊" class="toolbar-field-w-md" />
            </el-form-item>
            <el-form-item label="班级">
              <el-input v-model="filters.departmentName" clearable placeholder="支持模糊" class="toolbar-field-w-md" />
            </el-form-item>
            <el-form-item label="周期">
              <el-select v-model="filters.periodType" clearable placeholder="全部" class="toolbar-field-w-md">
                <el-option label="按周" value="Week" />
                <el-option label="按月" value="Month" />
                <el-option label="指定日期" value="DateRange" />
              </el-select>
            </el-form-item>
            <el-form-item>
              <el-button type="primary" size="small" icon="el-icon-search" @click="handleQuery">查询</el-button>
            </el-form-item>
          </div>
          <div class="toolbar-filter-actions">
            <el-button size="small" icon="el-icon-refresh-left" @click="resetFilters">重置</el-button>
            <el-button size="small" type="warning" icon="el-icon-download" :loading="exporting" @click="exportExcel">导出</el-button>
          </div>
        </el-form>
      </div>

      <div class="tbl-scroll-host">
        <el-table :data="competitions" v-loading="loading" border height="100%">
          <el-table-column prop="DepartmentName" label="班级" min-width="140" show-overflow-tooltip fixed="left" />
          <el-table-column prop="Name" label="竞赛小组" min-width="160" show-overflow-tooltip />
          <el-table-column label="周期" min-width="100" align="center">
            <template slot-scope="{ row }">
              <el-tag size="mini" effect="plain" :type="periodTagType(row.PeriodType)">
                {{ periodLabel(row.PeriodType) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="MemberCount" label="成员数" min-width="110" align="center" />
          <el-table-column label="操作" min-width="130" align="center" fixed="right">
            <template slot-scope="{ row }">
              <el-button type="text" class="table-op-link" @click="showRanking(row)">查看排行</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="pagination-container">
        <el-pagination
          layout="total, sizes, prev, pager, next"
          :total="pagination.total"
          :current-page.sync="pagination.page"
          :page-size.sync="pagination.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          @current-change="handlePageChange"
          @size-change="handleSizeChange"
        />
      </div>

      <el-dialog
        :title="rankingDialogTitle"
        :visible.sync="rankingDialog"
        width="860px"
        custom-class="cgc-ranking-dialog"
        append-to-body
      >
        <div class="ranking-bar">
          <div class="ranking-bar__left">
            <span class="ranking-bar__label">排行周期</span>
            <el-select
              v-model="selectedPeriodKey"
              size="small"
              placeholder="选择周期"
              class="ranking-period-select"
              @change="onRankingPeriodChange"
            >
              <el-option
                v-for="item in rankingPeriodOptions"
                :key="item.key"
                :label="item.label"
                :value="item.key"
              />
            </el-select>
          </div>
          <div class="ranking-bar__right">
            <template v-if="rankingMeta">
              <el-tag
                size="mini"
                effect="dark"
                :type="rankingMeta.IsFinalized ? 'info' : ''"
              >
                {{ rankingMeta.IsFinalized ? '已结束' : '进行中' }}
              </el-tag>
              <el-tag
                v-if="rankingMeta.IsPartialPeriod"
                type="warning"
                size="mini"
                effect="plain"
              >
                {{ rankingMeta.PeriodRemark || '不足完整周期' }}
              </el-tag>
            </template>
            <el-button
              size="small"
              type="warning"
              icon="el-icon-download"
              :disabled="!ranking.length"
              @click="exportRankingExcel"
            >
              导出
            </el-button>
          </div>
        </div>
        <el-table :data="ranking" v-loading="rankingLoading" border height="420" class="ranking-table">
          <el-table-column label="排名" min-width="80" align="center">
            <template slot-scope="{ row }">
              <span class="rank-no" :class="'rank-no--' + Math.min(Number(row.Rank) || 99, 4)">
                {{ row.Rank || '-' }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="头像" min-width="80" align="center">
            <template slot-scope="{ row }">
              <oss-media-thumbs
                v-if="row.HeadPhotoImage"
                :media="row.HeadPhotoImage"
                thumb-width="36px"
                thumb-height="36px"
              />
              <span v-else class="student-avatar-empty">{{ studentNameInitial(row.StudentName) }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="StudentName" label="姓名" min-width="100" align="center" show-overflow-tooltip />
          <el-table-column label="号次" min-width="80" align="center">
            <template slot-scope="{ row }">{{ formatSeatNumber(row.SeatNumber) }}</template>
          </el-table-column>
          <el-table-column label="总分" min-width="100" align="center">
            <template slot-scope="{ row }">
              <span class="metric-pill metric-pill--score">{{ formatScore(row.TotalScore) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="表扬" min-width="90" align="center">
            <template slot-scope="{ row }">
              <span class="metric-pill metric-pill--praise">{{ row.PraiseCount || 0 }}</span>
            </template>
          </el-table-column>
          <el-table-column label="待改进" min-width="100" align="center">
            <template slot-scope="{ row }">
              <span class="metric-pill metric-pill--improve">{{ row.ImprovementCount || 0 }}</span>
            </template>
          </el-table-column>
        </el-table>
      </el-dialog>
    </section>
  </section>
</template>

<script>
import DepartmentPane from '../../components/DepartmentPane';
import {
  getClassGroupCompetitionSummary,
  getClassGroupCompetitionTeams,
  getClassGroupCompetitionStudentRanking,
  exportClassGroupCompetitions,
  exportClassGroupCompetitionStudentRanking,
} from '../../api/modules/moral-education';

export default {
  name: 'ClassGroupCompetitionSummary',
  components: { DepartmentPane },
  data() {
    return {
      departmentId: 0,
      className: '',
      competitions: [],
      ranking: [],
      rankingMeta: null,
      rankingDialog: false,
      rankingLoading: false,
      rankingTeamId: 0,
      rankingPeriodOptions: [],
      selectedPeriodKey: '',
      loading: false,
      exporting: false,
      activeCompetition: null,
      filters: { name: '', departmentName: '', periodType: '' },
      pagination: { page: 1, pageSize: 20, total: 0 },
    };
  },
  computed: {
    /**
     * 排行弹窗标题。
     */
    rankingDialogTitle() {
      const name = this.activeCompetition && this.activeCompetition.Name;
      return name ? `竞赛小组排行 · ${name}` : '竞赛小组排行';
    },
  },
  methods: {
    /**
     * 统一解析接口返回的业务数据。
     */
    responsePage(res) {
      return (res.data || res).response || { data: [], dataCount: 0 };
    },
    periodLabel(value) {
      if (value === 'Week') return '按周';
      if (value === 'Month') return '按月';
      if (value === 'DateRange') return '指定日期';
      return value || '-';
    },
    /**
     * 周期类型对应的 Tag 颜色。
     */
    periodTagType(value) {
      if (value === 'Week') return '';
      if (value === 'Month') return 'success';
      if (value === 'DateRange') return 'warning';
      return 'info';
    },
    formatDate(value) {
      return value ? String(value).slice(0, 10) : '-';
    },
    /**
     * 号次展示：无号次时留空。
     */
    formatSeatNumber(value) {
      const seat = Number(value);
      return seat > 0 ? seat : '';
    },
    /**
     * 总分展示。
     */
    formatScore(value) {
      if (value == null || value === '') return '0';
      const num = Number(value);
      return Number.isNaN(num) ? value : (Number.isInteger(num) ? num : num.toFixed(1));
    },
    /**
     * 无头像时用姓名首字占位。
     */
    studentNameInitial(name) {
      const text = String(name || '').trim();
      return text ? text.slice(0, 1) : '?';
    },
    formatDateValue(date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    },
    parseLocalDate(value) {
      if (!value) return null;
      if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
      const text = String(value).slice(0, 10);
      const parts = text.split('-').map(Number);
      if (parts.length !== 3 || parts.some((x) => Number.isNaN(x))) return null;
      return new Date(parts[0], parts[1] - 1, parts[2]);
    },
    /**
     * 取自然周周一。
     */
    getWeekMonday(value) {
      const date = this.parseLocalDate(value);
      if (!date) return null;
      const day = (date.getDay() + 6) % 7;
      return new Date(date.getFullYear(), date.getMonth(), date.getDate() - day);
    },
    /**
     * 生成竞赛范围内的排行周期选项；默认选中「当前」周期。
     */
    buildRankingPeriodOptions(competition) {
      const start = this.parseLocalDate(competition.StartDate);
      const end = this.parseLocalDate(competition.EndDate);
      if (!start || !end || end < start) return [];
      const today = this.parseLocalDate(new Date());
      const type = competition.PeriodType;
      const options = [];

      if (type === 'DateRange') {
        options.push({
          key: `${this.formatDateValue(start)}_${this.formatDateValue(end)}`,
          from: this.formatDateValue(start),
          to: this.formatDateValue(end),
          label: `指定日期（${this.formatDateValue(start)} 至 ${this.formatDateValue(end)}）`,
          badge: { text: '整段', type: 'warning' },
          isCurrent: today >= start && today <= end,
        });
        return options;
      }

      if (type === 'Month') {
        let cursor = new Date(start.getFullYear(), start.getMonth(), 1);
        const last = new Date(end.getFullYear(), end.getMonth(), 1);
        const currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const prevMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        while (cursor <= last) {
          const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
          const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
          const fromDate = monthStart < start ? start : monthStart;
          const toDate = monthEnd > end ? end : monthEnd;
          const isCurrent = monthStart.getTime() === currentMonth.getTime();
          const isPrev = monthStart.getTime() === prevMonth.getTime();
          let badge = null;
          let prefix = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`;
          if (isCurrent) {
            badge = { text: '本月', type: '' };
            prefix = `本月 · ${prefix}`;
          } else if (isPrev) {
            badge = { text: '上月', type: 'info' };
            prefix = `上月 · ${prefix}`;
          }
          options.push({
            key: `${this.formatDateValue(fromDate)}_${this.formatDateValue(toDate)}`,
            from: this.formatDateValue(fromDate),
            to: this.formatDateValue(toDate),
            label: `${prefix}（${this.formatDateValue(fromDate)} 至 ${this.formatDateValue(toDate)}）`,
            badge,
            isCurrent,
          });
          cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
        }
        return options;
      }

      // Week：按自然周切分，默认当前周。
      let weekStart = this.getWeekMonday(start);
      const lastWeekStart = this.getWeekMonday(end);
      const thisWeekStart = this.getWeekMonday(today);
      const lastWeekStartDate = thisWeekStart
        ? new Date(thisWeekStart.getFullYear(), thisWeekStart.getMonth(), thisWeekStart.getDate() - 7)
        : null;
      while (weekStart && weekStart <= lastWeekStart) {
        const naturalEnd = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + 6);
        const fromDate = weekStart < start ? start : weekStart;
        const toDate = naturalEnd > end ? end : naturalEnd;
        const isCurrent = thisWeekStart && weekStart.getTime() === thisWeekStart.getTime();
        const isPrev = lastWeekStartDate && weekStart.getTime() === lastWeekStartDate.getTime();
        let badge = null;
        let prefix = `${this.formatDateValue(fromDate)} 至 ${this.formatDateValue(toDate)}`;
        if (isCurrent) {
          badge = { text: '本周', type: '' };
          prefix = `本周（${this.formatDateValue(fromDate)} 至 ${this.formatDateValue(toDate)}）`;
        } else if (isPrev) {
          badge = { text: '上周', type: 'info' };
          prefix = `上周（${this.formatDateValue(fromDate)} 至 ${this.formatDateValue(toDate)}）`;
        }
        const dayCount = Math.round((toDate - fromDate) / 86400000) + 1;
        if (dayCount < 7) {
          prefix += ' · 不足一周';
        }
        options.push({
          key: `${this.formatDateValue(fromDate)}_${this.formatDateValue(toDate)}`,
          from: this.formatDateValue(fromDate),
          to: this.formatDateValue(toDate),
          label: prefix,
          badge,
          isCurrent,
        });
        weekStart = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + 7);
      }
      return options;
    },
    /**
     * 构建列表与导出共用的筛选参数。
     */
    buildQueryParams(pageSize) {
      return {
        schoolDepartmentId: this.departmentId,
        name: this.filters.name || undefined,
        departmentName: this.filters.departmentName || undefined,
        periodType: this.filters.periodType || undefined,
        page: pageSize <= 0 ? 1 : this.pagination.page,
        pageSize,
      };
    },
    /**
     * 点击家校通讯录节点后加载该部门范围内的竞赛汇总。
     */
    treeNodeClick(node) {
      this.departmentId = Number(node.value || node.id || 0);
      this.className = node.label || node.name || '';
      this.pagination.page = 1;
      this.load();
    },
    handleQuery() {
      if (!this.departmentId) return this.$message.warning('请先选择部门');
      this.pagination.page = 1;
      this.load();
    },
    resetFilters() {
      this.filters = { name: '', departmentName: '', periodType: '' };
      this.pagination.page = 1;
      this.load();
    },
    handlePageChange(page) {
      this.pagination.page = page;
      this.load();
    },
    handleSizeChange(size) {
      this.pagination.pageSize = size;
      this.pagination.page = 1;
      this.load();
    },
    /**
     * 按当前选中部门分页加载竞赛汇总列表。
     */
    async load() {
      if (!this.departmentId) return;
      this.loading = true;
      try {
        const page = this.responsePage(await getClassGroupCompetitionSummary(this.buildQueryParams(this.pagination.pageSize)));
        this.competitions = page.data || [];
        this.pagination.total = page.dataCount || 0;
      } finally {
        this.loading = false;
      }
    },
    /**
     * 查看指定竞赛小组的成员排行；默认打开当前周/月。
     */
    async showRanking(row) {
      this.activeCompetition = row;
      this.ranking = [];
      this.rankingMeta = null;
      this.rankingTeamId = 0;
      this.rankingPeriodOptions = this.buildRankingPeriodOptions(row);
      const current = this.rankingPeriodOptions.find((x) => x.isCurrent)
        || this.rankingPeriodOptions[this.rankingPeriodOptions.length - 1]
        || null;
      this.selectedPeriodKey = current ? current.key : '';
      const teams = ((await getClassGroupCompetitionTeams({ competitionId: row.Id })).data || {}).response || [];
      const sole = (teams || [])[0];
      if (!sole || !sole.TeamId) {
        this.rankingDialog = true;
        return this.$message.warning('该竞赛小组暂无成员');
      }
      this.rankingTeamId = sole.TeamId;
      this.rankingDialog = true;
      await this.loadRanking();
    },
    /**
     * 切换排行周期。
     */
    onRankingPeriodChange() {
      this.loadRanking();
    },
    /**
     * 下载后台导出的 Excel。
     */
    downloadExcelBlob(data, fileName) {
      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    /**
     * 解析导出失败时的后端提示。
     */
    async readExportError(data) {
      if (!data) return '';
      const mime = String(data.type || '');
      if (mime.indexOf('sheet') >= 0 || mime.indexOf('octet-stream') >= 0) return '';
      try {
        const text = await data.text();
        if (!text) return '导出失败';
        try {
          const json = JSON.parse(text);
          return json.msg || json.title || json.detail || '导出失败';
        } catch (e) {
          return text;
        }
      } catch (e) {
        return '导出失败';
      }
    },
    /**
     * 导出当前弹窗中的学生排行（后台接口）。
     */
    async exportRankingExcel() {
      if (!this.activeCompetition || !this.rankingTeamId) {
        return this.$message.warning('暂无导出数据');
      }
      const period = (this.rankingPeriodOptions || []).find((x) => x.key === this.selectedPeriodKey);
      if (!period) return this.$message.warning('请选择排行周期');
      try {
        const competitionName = (this.activeCompetition && this.activeCompetition.Name) || '竞赛小组';
        const departmentName = (this.activeCompetition && this.activeCompetition.DepartmentName) || this.className || '';
        const response = await exportClassGroupCompetitionStudentRanking({
          competitionId: this.activeCompetition.Id,
          teamId: this.rankingTeamId,
          from: period.from,
          to: period.to,
          competitionName,
          departmentName,
        });
        if (!response.data) return this.$message.error('导出数据为空');
        const err = await this.readExportError(response.data);
        if (err) return this.$message.error(err);
        this.downloadExcelBlob(
          response.data,
          `竞赛小组排行_${competitionName}_${period.from}_${period.to}.xlsx`
        );
      } catch (e) {
        this.$message.error('导出出错');
      }
    },
    /**
     * 按选中周期加载学生排行。
     */
    async loadRanking() {
      if (!this.activeCompetition || !this.rankingTeamId) return;
      const period = (this.rankingPeriodOptions || []).find((x) => x.key === this.selectedPeriodKey);
      if (!period) {
        this.ranking = [];
        this.rankingMeta = null;
        return;
      }
      this.rankingLoading = true;
      try {
        const response = await getClassGroupCompetitionStudentRanking({
          competitionId: this.activeCompetition.Id,
          teamId: this.rankingTeamId,
          from: period.from,
          to: period.to,
        });
        const data = (response.data || response).response || {};
        this.ranking = data.Items || [];
        this.rankingMeta = {
          PeriodStart: data.PeriodStart,
          PeriodEnd: data.PeriodEnd,
          IsFinalized: !!data.IsFinalized,
          IsPartialPeriod: !!data.IsPartialPeriod,
          PeriodRemark: data.PeriodRemark || '',
          PeriodDayCount: data.PeriodDayCount || 0,
        };
      } finally {
        this.rankingLoading = false;
      }
    },
    /**
     * 导出全部筛选结果（后台接口）。
     */
    async exportExcel() {
      if (!this.departmentId) return this.$message.warning('请先选择部门');
      this.exporting = true;
      try {
        const response = await exportClassGroupCompetitions(this.buildQueryParams(0));
        if (!response.data) return this.$message.error('导出数据为空');
        const err = await this.readExportError(response.data);
        if (err) return this.$message.error(err);
        this.downloadExcelBlob(response.data, `竞赛汇总_${this.className || '列表'}.xlsx`);
      } catch (e) {
        this.$message.error('导出出错');
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<style scoped>
.cgc-summary {
  height: 100%;
}
.ranking-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 14px;
  padding: 10px 12px;
  background: #f7f8fa;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}
.ranking-bar__left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
}
.ranking-bar__label {
  flex-shrink: 0;
  color: #606266;
  font-size: 13px;
  font-weight: 600;
}
.ranking-period-select {
  width: 100%;
  max-width: 420px;
}
.ranking-bar__right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.ranking-table {
  width: 100%;
}
.rank-no {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 6px;
  border-radius: 12px;
  background: #f0f2f5;
  color: #606266;
  font-size: 13px;
  font-weight: 600;
}
.rank-no--1 {
  background: #fdf6ec;
  color: #e6a23c;
}
.rank-no--2 {
  background: #f4f4f5;
  color: #909399;
}
.rank-no--3 {
  background: #fdf2e9;
  color: #c8783a;
}
.student-avatar-empty {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #ecf5ff;
  color: #409eff;
  font-size: 14px;
  font-weight: 600;
}
.metric-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 26px;
  padding: 0 10px;
  border-radius: 13px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
}
.metric-pill--score {
  background: #ecf5ff;
  color: #409eff;
}
.metric-pill--praise {
  background: #f0f9eb;
  color: #67c23a;
}
.metric-pill--improve {
  background: #fef0f0;
  color: #f56c6c;
}
</style>

<style>
.cgc-ranking-dialog {
  margin-top: 8vh !important;
}
.cgc-ranking-dialog .el-dialog__body {
  padding-top: 12px;
  padding-bottom: 20px;
}
.cgc-ranking-dialog .el-table__empty-block {
  min-height: 360px;
}
</style>
